/**
 * Parser for ArchiMate PlantUML diagrams (plantuml-stdlib/Archimate-PlantUML syntax).
 *
 * Supported constructs:
 *   - Element declarations: Category_ElementType(id, "label")
 *   - Relationships: Rel_Type(from, to, "label") and Rel_Type_Direction(from, to, "label")
 *   - Groups: Group(id, "label") { ... } / Grouping / Other_Grouping
 *   - @startuml / @enduml block
 *   - !include / !theme / comment lines (skipped)
 */

import { ARCHIMATE_ELEMENTS } from '../types/archimateElements';

// ─── Mappings ────────────────────────────────────────────────────────────────

/** PlantUML macro name → ARCHIMATE_ELEMENTS key */
const MACRO_TO_TYPE = {
  // Strategy
  Strategy_Resource: 'RESOURCE',
  Strategy_Capability: 'CAPABILITY',
  Strategy_CourseOfAction: 'COURSE_OF_ACTION',
  Strategy_ValueStream: 'VALUE_STREAM',
  // Business
  Business_Actor: 'BUSINESS_ACTOR',
  Business_Role: 'BUSINESS_ROLE',
  Business_Collaboration: 'BUSINESS_COLLABORATION',
  Business_Interface: 'BUSINESS_INTERFACE',
  Business_Contract: 'CONTRACT',
  Business_Process: 'BUSINESS_PROCESS',
  Business_Function: 'BUSINESS_FUNCTION',
  Business_Interaction: 'BUSINESS_INTERACTION',
  Business_Event: 'BUSINESS_EVENT',
  Business_Service: 'BUSINESS_SERVICE',
  Business_Object: 'BUSINESS_OBJECT',
  Business_Representation: 'REPRESENTATION',
  Business_Product: 'PRODUCT',
  // Application
  Application_Component: 'APPLICATION_COMPONENT',
  Application_Collaboration: 'APPLICATION_COLLABORATION',
  Application_Interface: 'APPLICATION_INTERFACE',
  Application_Function: 'APPLICATION_FUNCTION',
  Application_Interaction: 'APPLICATION_INTERACTION',
  Application_Process: 'APPLICATION_PROCESS',
  Application_Event: 'APPLICATION_EVENT',
  Application_Service: 'APPLICATION_SERVICE',
  Application_DataObject: 'DATA_OBJECT',
  // Technology
  Technology_Node: 'NODE',
  Technology_Device: 'DEVICE',
  Technology_SystemSoftware: 'SYSTEM_SOFTWARE',
  Technology_Collaboration: 'TECHNOLOGY_COLLABORATION',
  Technology_Interface: 'TECHNOLOGY_INTERFACE',
  Technology_Function: 'TECHNOLOGY_FUNCTION',
  Technology_Process: 'TECHNOLOGY_PROCESS',
  Technology_Service: 'TECHNOLOGY_SERVICE',
  Technology_Artifact: 'ARTIFACT',
  Technology_CommunicationNetwork: 'TECHNOLOGY_COLLABORATION',
  Technology_Path: 'ARTIFACT',
  // Physical
  Physical_Equipment: 'EQUIPMENT',
  Physical_Facility: 'FACILITY',
  Physical_Material: 'MATERIAL',
  // Implementation
  Implementation_WorkPackage: 'WORK_PACKAGE',
  Implementation_Deliverable: 'DELIVERABLE',
  Implementation_Plateau: 'PLATEAU',
  Implementation_Gap: 'GAP',
  Implementation_ImplementationEvent: 'WORK_PACKAGE',
  // Motivation
  Motivation_Stakeholder: 'STAKEHOLDER',
  Motivation_Driver: 'DRIVER',
  Motivation_Assessment: 'ASSESSMENT',
  Motivation_Goal: 'GOAL',
  Motivation_Outcome: 'OUTCOME',
  Motivation_Principle: 'PRINCIPLE',
  Motivation_Requirement: 'REQUIREMENT',
  Motivation_Constraint: 'CONSTRAINT',
  Motivation_Value: 'VALUE',
};

/**
 * Relationship type (direction-stripped) → arrow properties.
 * Available caps in the canvas: 'none' | 'arrow' | 'diamond' | 'circle'
 */
const REL_TO_ARROW = {
  Access:          { dashed: true,  startCap: 'none',   endCap: 'arrow'   },
  Access_r:        { dashed: true,  startCap: 'arrow',  endCap: 'none'    },
  Access_w:        { dashed: true,  startCap: 'none',   endCap: 'arrow'   },
  Access_rw:       { dashed: true,  startCap: 'arrow',  endCap: 'arrow'   },
  Aggregation:     { dashed: false, startCap: 'none',   endCap: 'diamond' },
  Assignment:      { dashed: false, startCap: 'circle', endCap: 'arrow'   },
  Association:     { dashed: false, startCap: 'none',   endCap: 'none'    },
  Association_dir: { dashed: false, startCap: 'none',   endCap: 'arrow'   },
  Composition:     { dashed: false, startCap: 'none',   endCap: 'diamond' },
  Flow:            { dashed: true,  startCap: 'none',   endCap: 'arrow'   },
  Influence:       { dashed: true,  startCap: 'none',   endCap: 'arrow'   },
  Realization:     { dashed: true,  startCap: 'none',   endCap: 'arrow'   },
  Serving:         { dashed: false, startCap: 'none',   endCap: 'arrow'   },
  Specialization:  { dashed: false, startCap: 'none',   endCap: 'arrow'   },
  Triggering:      { dashed: false, startCap: 'none',   endCap: 'arrow'   },
};

const LAYER_ORDER = [
  'motivation', 'strategy', 'business',
  'application', 'technology', 'physical', 'implementation',
];

const LAYER_LABEL = {
  motivation: 'Motivation',
  strategy: 'Strategy',
  business: 'Business',
  application: 'Application',
  technology: 'Technology',
  physical: 'Physical',
  implementation: 'Implementation',
  other: 'Other',
};

// ─── Detection ───────────────────────────────────────────────────────────────

/** Returns true when `code` contains ArchiMate PlantUML syntax (not sequence diagrams). */
export const isArchimatePlantUmlCode = (code) =>
  /!include\s+<archimate\/Archimate>/i.test(code) ||
  /(?:Business|Application|Technology|Physical|Implementation|Motivation|Strategy)_\w+\s*\(/i.test(code) ||
  /^Rel_(?:Composition|Aggregation|Assignment|Realization|Serving|Access|Flow|Influence|Triggering|Specialization|Association)/im.test(code);

// ─── Parser ──────────────────────────────────────────────────────────────────

/**
 * Parse ArchiMate PlantUML syntax and return canvas elements.
 *
 * @param {string} text - Raw PlantUML code
 * @param {object} ElementFactory - Factory from createThemedElementFactory
 * @returns {Array} Canvas elements ready to render
 */
export const parseArchimatePlantUml = (text, ElementFactory) => {
  const lines = (text || '').split(/\r?\n/).map(l => l.trim());

  /** pumlId → { archimateType: string, label: string } */
  const elements = {};
  const orderedIds = [];
  /** { from, to, relType, label } */
  const relations = [];
  /** { id, label, children: Set<pumlId> } */
  const groups = [];

  // Regexes
  // Element: Category_Type(id, "label" [, extra...])
  const elementRe = /^([A-Za-z]+_[A-Za-z][A-Za-z_]*)\s*\(([^)]*)\)/;
  // Relation: Rel_Type[_Direction](from, to, "label" [, extra...])
  const relRe = /^Rel_([A-Za-z_]+)\s*\(\s*(\w+)\s*,\s*(\w+)\s*,\s*"([^"]*)"/;
  // Group declarations
  const groupRe = /^(?:Group|Grouping|Other_Grouping)\s*\(\s*(\w+)\s*,\s*"([^"]*)"\s*\)/;

  let inBlock = false;
  const groupStack = [];

  for (const line of lines) {
    if (/^@startuml/i.test(line)) { inBlock = true; continue; }
    if (/^@enduml/i.test(line))   { inBlock = false; continue; }
    if (!inBlock) continue;
    // Skip directives and comments
    if (line.startsWith('!') || line.startsWith("'") || line === '') continue;

    // Group close
    if (line === '}') { groupStack.pop(); continue; }

    let m;

    // Group open (check before element — Other_Grouping looks like Category_Type)
    if ((m = groupRe.exec(line))) {
      const group = { id: m[1], label: m[2], children: new Set() };
      groups.push(group);
      groupStack.push(group);
      continue;
    }

    // Relation (check before element to avoid matching Rel_ as an element macro)
    if ((m = relRe.exec(line))) {
      // Strip trailing direction (Up|Down|Left|Right); keep access variants (r/w/rw)
      const relType = m[1].replace(/_(?:Up|Down|Left|Right)$/i, '');
      relations.push({ from: m[2], to: m[3], relType, label: m[4] });
      continue;
    }

    // Element declaration
    if ((m = elementRe.exec(line))) {
      const macro = m[1];
      const archimateType = MACRO_TO_TYPE[macro];
      if (!archimateType) continue; // unknown or group macro

      // Parse args: first is id, second (quoted) is label
      const rawArgs = m[2].split(',');
      const id = rawArgs[0]?.trim();
      const label = rawArgs[1]?.trim().replace(/^"|"$/g, '') || id;

      if (id && !elements[id]) {
        elements[id] = { archimateType, label };
        orderedIds.push(id);
        if (groupStack.length > 0) {
          groupStack[groupStack.length - 1].children.add(id);
        }
      }
    }
  }

  // ─── Layout ─────────────────────────────────────────────────────────────

  const EL_W = 160;
  const EL_H = 80;
  const COL_GAP = 30;
  const ROW_GAP = 70;
  const LAYER_LABEL_H = 22;
  const BASE_X = 60;
  const BASE_Y = 60;

  // Bucket elements by layer (preserving orderedIds order within each bucket)
  const byLayer = {};
  for (const id of orderedIds) {
    const def = ARCHIMATE_ELEMENTS[elements[id].archimateType];
    const layer = def?.layer || 'other';
    (byLayer[layer] = byLayer[layer] || []).push(id);
  }

  const presentLayers = [
    ...LAYER_ORDER.filter(l => byLayer[l]?.length > 0),
    ...(byLayer.other?.length > 0 ? ['other'] : []),
  ];

  // Compute element positions
  const positions = {};
  let curY = BASE_Y;
  for (const layer of presentLayers) {
    curY += LAYER_LABEL_H + 8;
    let curX = BASE_X;
    for (const id of byLayer[layer]) {
      positions[id] = { x: curX, y: curY };
      curX += EL_W + COL_GAP;
    }
    curY += EL_H + ROW_GAP;
  }

  // ─── Build canvas output ─────────────────────────────────────────────────

  const backgroundEls = [];
  const archimateEls = [];
  const arrowEls = [];
  const idToEl = {};

  // 1. Layer labels
  let labelY = BASE_Y;
  for (const layer of presentLayers) {
    const ids = byLayer[layer];
    const totalW = ids.length * EL_W + (ids.length - 1) * COL_GAP;
    const labelEl = ElementFactory.text(BASE_X + totalW / 2, labelY + LAYER_LABEL_H / 2, `« ${LAYER_LABEL[layer] || layer} »`);
    labelEl.fontSize = 13;
    labelEl.fill = '#666666';
    backgroundEls.push(labelEl);
    labelY += LAYER_LABEL_H + 8 + EL_H + ROW_GAP;
  }

  // 2. Group background rectangles
  for (const group of groups) {
    const childIds = [...group.children].filter(id => positions[id]);
    if (childIds.length === 0) continue;

    const xs = childIds.map(id => positions[id].x);
    const ys = childIds.map(id => positions[id].y);
    const pad = 16;
    const gx = Math.min(...xs) - pad;
    const gy = Math.min(...ys) - pad - 18;
    const gw = Math.max(...xs) + EL_W - gx + pad;
    const gh = Math.max(...ys) + EL_H - gy + pad;

    const groupRect = ElementFactory.rectangle(gx, gy, gw, gh, {
      fill: 'rgba(180,180,180,0.08)',
      stroke: '#999999',
      strokeWidth: 1.5,
      strokeDasharray: '6,4',
      borderRadius: 8,
    });
    const groupLabel = ElementFactory.text(gx + gw / 2, gy + 11, group.label);
    groupLabel.fontSize = 12;
    groupLabel.fill = '#666666';
    backgroundEls.push(groupRect, groupLabel);
  }

  // 3. ArchiMate elements
  for (const id of orderedIds) {
    const { archimateType, label } = elements[id];
    const pos = positions[id];
    if (!pos) continue;
    const el = ElementFactory.archimate(archimateType, pos.x, pos.y, EL_W, EL_H, label);
    idToEl[id] = el;
    archimateEls.push(el);
  }

  // 4. Relation arrows
  for (const rel of relations) {
    const fromPos = positions[rel.from];
    const toPos = positions[rel.to];
    if (!fromPos || !toPos || !idToEl[rel.from] || !idToEl[rel.to]) continue;

    const x1 = fromPos.x + EL_W / 2;
    const y1 = fromPos.y + EL_H / 2;
    const x2 = toPos.x + EL_W / 2;
    const y2 = toPos.y + EL_H / 2;

    const style = REL_TO_ARROW[rel.relType] || REL_TO_ARROW.Association;
    const arrow = ElementFactory.arrow(x1, y1, x2, y2);
    arrow.startCap = style.startCap;
    arrow.endCap = style.endCap;
    arrow.dashed = style.dashed;
    arrowEls.push(arrow);

    if (rel.label) {
      const labelEl = ElementFactory.text((x1 + x2) / 2, (y1 + y2) / 2 - 12, rel.label);
      labelEl.fontSize = 12;
      arrowEls.push(labelEl);
    }
  }

  return [...backgroundEls, ...archimateEls, ...arrowEls];
};
